/**
 * Повний редактор вхідної ПН (2026-07-28) — реєстрація отриманої від
 * постачальника ПН (замість read-only viewer'а + модалки «з приходу»).
 *
 * № і дата — З САМОЇ ПН постачальника (не наша нумерація). Рядки з
 * розкладкою за ставками; «заповнити з приходу» — попап проведених
 * оприбуткувань постачальника. Прапор «зареєстрована в ЄРПН» + дата —
 * підстава включення до податкового кредиту.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  taxInvoicesIncoming,
  type TaxInvoiceItemIn,
} from '@/api/taxInvoices';
import { goodsReceipts } from '@/api/goodsReceipts';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import SearchSelect from '@/components/SearchSelect';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
import QuickNomenclatureBatchModal, {
  type BatchNomInitialRow,
} from '@/shared/components/QuickNomenclatureBatchModal';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const fmt = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Row extends TaxInvoiceItemIn { _key: string }

let _rowSeq = 0;
const newKey = () => `ri${++_rowSeq}`;

interface FormState {
  organization_id: string;
  invoice_number: string;
  invoice_date: string;
  contractor_id: string;
  contract_id: string;
  is_registered_erpn: boolean;
  registration_date: string;
  comment: string;  // ч.889
  base_receipt_id: string;
  // ч.906: підстава іншого типу (з 1С: платіжка/РКО/авансовий звіт/ВМД/
  // замовлення) — зберігаємо і показуємо, не губимо при збереженні.
  base_other_type: string;
  base_other_id: string;
  items: Row[];
}

const OTHER_BASE_LABEL: Record<string, string> = {
  'doc.bank_payments': 'платіжка',
  'doc.cash_payments': 'видатковий касовий ордер',
  'doc.advance_reports': 'авансовий звіт',
  'doc.customs_declarations': 'ВМД',
  'doc.supplier_orders': 'рахунок постачальника',
  'doc.goods_returns_to_supplier': 'повернення постачальнику',
};

const EMPTY: FormState = {
  organization_id: '',
  invoice_number: '',
  invoice_date: new Date().toISOString().slice(0, 10),
  contractor_id: '',
  contract_id: '',
  is_registered_erpn: true,
  comment: '',
  registration_date: '',
  base_receipt_id: '',
  base_other_type: '',
  base_other_id: '',
  items: [],
};

export default function TaxInvoiceIncomingEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const [f, setF] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [rcpPopup, setRcpPopup] = useState(false);
  const [batchNomOpen, setBatchNomOpen] = useState(false);

  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('tax.invoice.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', f.organization_id, f.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: f.organization_id, contractor_id: f.contractor_id,
    }),
    enabled: !!f.organization_id && !!f.contractor_id,
  });
  const vatRatesQ = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });

  const docQ = useQuery({
    queryKey: ['tax-invoice-incoming', id],
    queryFn: () => taxInvoicesIncoming.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(f, setF, { ready: isNew || !!docQ.data });
  const d = docQ.data;

  useEffect(() => {
    if (!d) return;
    setF({
      organization_id: d.organization_id,
      invoice_number: d.invoice_number,
      invoice_date: d.invoice_date,
      contractor_id: d.contractor_id,
      contract_id: d.contract_id ?? '',
      is_registered_erpn: d.is_registered_erpn,
      registration_date: d.registration_date ?? '',
      comment: d.comment ?? '',
      base_receipt_id: d.base_document_type === 'doc.goods_receipts'
        ? (d.base_document_id ?? '') : '',
      base_other_type: d.base_document_type && d.base_document_type !== 'doc.goods_receipts'
        ? d.base_document_type : '',
      base_other_id: d.base_document_type && d.base_document_type !== 'doc.goods_receipts'
        ? (d.base_document_id ?? '') : '',
      items: d.items.map((it) => ({
        _key: newKey(),
        nomenclature_id: it.nomenclature_id,
        description: it.description,
        ukt_zed_code: it.ukt_zed_code,
        uom_code: it.uom_code,
        quantity: it.quantity,
        price_without_vat: it.price_without_vat,
        vat_rate_id: it.vat_rate_id,
        base_amount: it.base_amount,
        vat_amount: it.vat_amount,
      })),
    });
  }, [d]);

  // ?basis=<receipt_id> — кнопка «створити вхідну ПН» з прихода (2026-07-29).
  const basisApplied = useRef(false);
  const basisId = searchParams.get('basis');
  useEffect(() => {
    if (!isNew || !basisId || basisApplied.current) return;
    if (!nomListQ.data || !vatRatesQ.data) return;
    basisApplied.current = true;
    void fillFromReceipt(basisId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, basisId, nomListQ.data, vatRatesQ.data]);

  useEffect(() => {
    if (!isNew || f.organization_id || !availableOrgs.length) return;
    const preferred = availableOrgs.find((o) => o.id === defaultOrgId) ?? availableOrgs[0];
    if (preferred) setF((s) => ({ ...s, organization_id: preferred.id }));
  }, [isNew, availableOrgs, defaultOrgId, f.organization_id]);

  const nomById = useMemo(() => {
    const m = new Map<string, { name: string; ukt?: string | null }>();
    nomListQ.data?.items.forEach((n) => m.set(n.id, { name: n.name, ukt: n.ukt_zed_code }));
    return m;
  }, [nomListQ.data]);

  const supplier = contractorsQ.data?.items.find((c) => c.id === f.contractor_id);
  // Підстава (прихід): номер для показу + посилання.
  const baseRcpQ = useQuery({
    queryKey: ['goods-receipt-brief', f.base_receipt_id],
    queryFn: () => goodsReceipts.get(f.base_receipt_id),
    enabled: !!f.base_receipt_id,
  });
  const rates = vatRatesQ.data ?? [];
  const rate20 = rates.find((r) => r.code === 'VAT_20');
  const rateById = useMemo(() => {
    const m = new Map<string, { code: string; pct: number }>();
    rates.forEach((r) => m.set(r.id, { code: r.code, pct: Number(r.rate_percent) }));
    return m;
  }, [rates]);

  function lineAmounts(r: Row): { base: number; vat: number } {
    // ч.891: підсумковий рядок (кількість 0 — так 1С УТП зберігає вхідну ПН:
    // лише суми) — база/ПДВ з рядка, а не кількість × ціна.
    const q = Number(r.quantity) || 0;
    const rate = rateById.get(r.vat_rate_id);
    const taxable = !!rate && ['VAT_20', 'VAT_7', 'VAT_14'].includes(rate.code);
    if (q === 0 && r.base_amount != null && r.base_amount !== '') {
      const base = Math.round(Number(r.base_amount) * 100) / 100;
      const vat = r.vat_amount != null && r.vat_amount !== ''
        ? Math.round(Number(r.vat_amount) * 100) / 100
        : (taxable ? Math.round(base * rate!.pct) / 100 : 0);
      return { base, vat };
    }
    const base = Math.round(q * (Number(r.price_without_vat) || 0) * 100) / 100;
    const vat = taxable ? Math.round(base * rate!.pct) / 100 : 0;
    return { base, vat };
  }
  const totals = useMemo(() => {
    let base = 0, vat = 0;
    for (const r of f.items) {
      const a = lineAmounts(r); base += a.base; vat += a.vat;
    }
    return { base, vat, total: base + vat };
  }, [f.items, rateById]);

  // ч.1347: і збережені, ще не проведені надходження (ПН реєструється «на підставі»).
  const receiptsQ = useQuery({
    queryKey: ['gr-for-pn', f.organization_id, f.contractor_id],
    queryFn: () => goodsReceipts.list({
      organization_id: f.organization_id || undefined,
      contractor_id: f.contractor_id || undefined,
      limit: 200,
    }),
    enabled: rcpPopup,
  });

  async function fillFromReceipt(receiptId: string) {
    try {
      const rc = await goodsReceipts.get(receiptId);
      setF((s) => ({
        ...s,
        organization_id: rc.organization_id,
        contractor_id: rc.contractor_id,
        contract_id: (rc as any).contract_id ?? '',
        base_receipt_id: receiptId,
        invoice_date: (rc.document_date ?? s.invoice_date).slice(0, 10),
        items: (rc.items ?? []).map((it: any) => ({
          _key: newKey(),
          nomenclature_id: it.nomenclature_id,
          description: nomById.get(it.nomenclature_id)?.name ?? '',
          ukt_zed_code: nomById.get(it.nomenclature_id)?.ukt ?? null,
          uom_code: null,
          quantity: String(it.quantity),
          price_without_vat: String(it.price),
          vat_rate_id: it.vat_rate_id,
        })),
      }));
      setRcpPopup(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    }
  }

  function setRow(key: string, patch: Partial<Row>) {
    setF((s) => ({
      ...s,
      items: s.items.map((r) => (r._key === key ? { ...r, ...patch } : r)),
    }));
  }
  function addRow() {
    setF((s) => ({
      ...s,
      items: [...s.items, {
        _key: newKey(), nomenclature_id: null, description: '',
        ukt_zed_code: null, uom_code: null, quantity: '1',
        price_without_vat: '0', vat_rate_id: rate20?.id ?? '',
      }],
    }));
  }

  function buildPayload() {
    if (!f.organization_id) throw new Error('Оберіть організацію');
    if (!f.contractor_id) throw new Error('Оберіть постачальника');
    if (!f.invoice_number.trim()) throw new Error('Вкажіть № ПН постачальника');
    const items = f.items
      .filter((r) => r.description.trim() || r.nomenclature_id)
      .map(({ _key, ...r }) => ({
        ...r,
        description: r.description.trim()
          || (r.nomenclature_id ? (nomById.get(r.nomenclature_id)?.name ?? '—') : '—'),
      }));
    if (!items.length) throw new Error('Додайте хоча б один рядок');
    return {
      organization_id: f.organization_id,
      invoice_number: f.invoice_number.trim(),
      invoice_date: f.invoice_date,
      contractor_id: f.contractor_id,
      contract_id: f.contract_id || null,
      is_registered_erpn: f.is_registered_erpn,
      registration_date: f.registration_date || null,
      comment: f.comment.trim() || null,
      base_document_type: f.base_receipt_id ? 'doc.goods_receipts'
        : (f.base_other_id ? f.base_other_type : null),
      base_document_id: f.base_receipt_id || f.base_other_id || null,
      items,
    };
  }

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
    qc.invalidateQueries({ queryKey: ['tax-invoice-incoming', id] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const p = buildPayload();
      if (isNew) return taxInvoicesIncoming.createManual(p);
      const { organization_id: _o, ...upd } = p;
      return taxInvoicesIncoming.update(id!, upd);
    },
    onSuccess: (res) => {
      setError(null); invalidate();
      if (isNew) navigate(`/tax-invoices-incoming/${res.id}`, { replace: true });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const p = buildPayload();
      const doc = isNew
        ? await taxInvoicesIncoming.createManual(p)
        : await taxInvoicesIncoming.update(id!, (() => { const { organization_id: _o, ...u } = p; return u; })());
      try {
        return await taxInvoicesIncoming.post(doc.id);
      } catch (e) {
        // ч.1343: новий документ уже створено — відкриваємо його, інакше повторне «провести» створило б дубль.
        if (isNew) navigate(`/tax-invoices-incoming/${doc.id}`, { replace: true });
        throw e;
      }
    },
    onSuccess: (res) => {
      setError(null); invalidate();
      navigate(`/tax-invoices-incoming/${res.id}`, { replace: true });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  const unpost = useMutation({
    mutationFn: () => taxInvoicesIncoming.unpost(id!),
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });
  const remove = useMutation({
    mutationFn: () => taxInvoicesIncoming.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tax-invoices-incoming'] });
      navigate('/tax-invoices-incoming');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e)),
  });

  // Посилання на кориговану ПН (2026-08-07): шукаємо серед вхідних того ж
  // постачальника за №+датою з XML РК. ХУК — ДО ранніх return'ів
  // (React #310, live-прецедент 2026-08-07).
  const corr = d as (typeof d & {
    corrected_number?: string | null; corrected_date?: string | null;
  }) | undefined;
  const correctedQ = useQuery({
    queryKey: ['inc-corrected-pn', d?.contractor_id, corr?.corrected_number],
    queryFn: () => taxInvoicesIncoming.list({
      contractor_id: d!.contractor_id, limit: 500 }),
    enabled: !!d?.is_correction && !!corr?.corrected_number && !!d?.contractor_id,
  });
  const correctedDoc = (correctedQ.data?.items ?? []).find((x) =>
    !x.is_correction && x.invoice_number === corr?.corrected_number
    && (!corr?.corrected_date || x.invoice_date === corr.corrected_date));

  if (!isNew && !d) return <div className="card">Завантаження…</div>;
  if (isNew && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        У вас немає прав реєструвати ПН у жодній з {totalAccessibleOrgs} ваших організацій.
      </div>
    );
  }

  const isPosted = !!d?.posted;
  const readOnly = isPosted;

  // ч.1352 (обернений ланцюг): надходження можна створити З вхідної ПН (накладну з ЄРПН
  // прийняли раніше, ніж оформили прихід); створене надходження стає її підставою.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] =
    isNew || d?.is_correction ? [] : [
      ...(d?.base_document_id ? [{
        label: 'відкрити документ-підставу',
        title: 'Надходження (документ), на підставі якого ця ПН',
        onClick: () => {
          const to = d.base_document_type ? DOC_ROUTES[d.base_document_type]?.(d.base_document_id!) : undefined;
          if (to) navigate(to);
        },
      }] : [{
        label: 'створити надходження',
        title: 'Надходження товарів і послуг з рядків цієї ПН; накладна стане його вхідною ПН',
        onClick: () => navigate(`/goods-receipts/new?tax_invoice=${id}`),
      }]),
      // ч.1359: з вхідної ПН — і рахунок постачальника, і замовлення постачальнику.
      { label: 'створити рахунок постачальника', title: 'Рахунок постачальника з рядків цієї ПН',
        onClick: () => navigate(`/supplier-invoices/new?tax_invoice_in=${id}`) },
      { label: 'створити замовлення постачальнику', title: 'Замовлення постачальнику з рядків цієї ПН',
        onClick: () => navigate(`/supplier-orders/new?tax_invoice_in=${id}`) },
    ];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (f.organization_id ? 1 : 0) + (f.invoice_date ? 1 : 0) + (f.invoice_number.trim() ? 1 : 0) },
    { id: 'party', title: 'Постачальник і підстава', required: 1, filled: f.contractor_id ? 1 : 0 },
    { id: 'items', title: 'Рядки', required: 1, filled: f.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова вхідна ПН (від постачальника)'
            : `${d!.is_correction ? 'РК (вхідний)' : 'Вхідна ПН'} №${d!.invoice_number} від ${new Date(d!.invoice_date).toLocaleDateString('uk-UA')}`}
        </h2>
        <div style={{ marginLeft: 'auto' }}>
          <DocumentToolbar
            entriesDoc={{ type: 'tax.tax_invoices_incoming', id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
            isNew={isNew}
            onBackToList={() => navigate('/tax-invoices-incoming')}
            onSave={() => save.mutate()}
            saveDisabled={readOnly}
            savePending={save.isPending}
            isPosted={isPosted}
            onPost={() => saveAndPost.mutate()}
            onUnpost={() => unpost.mutate()}
            postPending={saveAndPost.isPending}
            unpostPending={unpost.isPending}
            postTitle="Зберігає та проводить: податковий кредит у регістр ПДВ"
            onPrint={!isNew ? () => navigate(`/tax-invoices-incoming/${id}/print`) : undefined}
            deletable={!isPosted && !isNew}
            onDelete={() => {
              confirm('Видалити вхідну ПН?').then((ok) => { if (ok) remove.mutate(); });
            }}
          />
        </div>
      </div>


      {error && (
        <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'tax.tax_invoices_incoming', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{d?.is_correction ? 'Дата РК' : 'Дата ПН'} *</label>
          <DatePopover value={f.invoice_date}
            onChange={(v) => setF((s) => ({ ...s, invoice_date: v }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Організація (отримувач) *</label>
          <select value={f.organization_id} disabled={!isNew}
            onChange={(e) => setF((s) => ({
              ...s, organization_id: e.target.value, contract_id: '',
            }))}>
            <option value="">— оберіть —</option>
            {(isNew ? availableOrgs : (orgListQ.data?.items ?? [])).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{d?.is_correction ? '№ РК постачальника' : '№ ПН постачальника'} *</label>
          <input value={f.invoice_number} disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, invoice_number: e.target.value }))} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={2} title="Постачальник і підстава">
      <div className="doc-grid" style={{ alignItems: 'end' }}>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>
            Постачальник *
            {supplier && (
              <span style={{ marginLeft: 8, color: 'var(--text-secondary)', fontWeight: 400 }}>
                {supplier.vat_number ? `ІПН ${supplier.vat_number}` : 'без ІПН'}
                {supplier.edrpou ? ` · ЄДРПОУ ${supplier.edrpou}` : ''}
              </span>
            )}
          </label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou,
            }))}
            value={f.contractor_id}
            disabled={readOnly || !!f.base_receipt_id}
            onChange={(cid) => setF((s) => ({ ...s, contractor_id: cid, contract_id: '' }))}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Договір</label>
          <select value={f.contract_id}
            disabled={readOnly || !f.contractor_id || !!f.base_receipt_id}
            onChange={(e) => setF((s) => ({ ...s, contract_id: e.target.value }))}>
            <option value="">без договору</option>
            {(contractsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          {/* 2026-07-31 (рішення власника): звʼязок блокує Постачальник/Договір;
              ч.889 — канон підстави ч.861. */}
          <label>{(f.base_receipt_id || f.base_other_id) ? 'Підстава' : '\u00A0'}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {!f.base_receipt_id && f.base_other_id && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                  onClick={() => {
                    const r = DOC_ROUTES[f.base_other_type]?.(f.base_other_id);
                    if (r) navigate(r);
                  }}>
                  {OTHER_BASE_LABEL[f.base_other_type] ?? 'документ-підстава'}
                </a>
                {!readOnly && (
                  <button type="button" className="btn-x"
                    onClick={() => setF((s) => ({ ...s, base_other_type: '', base_other_id: '' }))}
                    title="Зняти звʼязок з підставою">×</button>
                )}
              </span>
            )}
            {!f.base_receipt_id && !f.base_other_id && (
            <button type="button" className="btn-doc" disabled={readOnly}
              onClick={() => setRcpPopup(true)}>
              <DocIcon />заповнити з приходу
            </button>
            )}
            {f.base_receipt_id && (
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                  onClick={() => navigate(`/goods-receipts/${f.base_receipt_id}`)}>
                  прихід {baseRcpQ.data
                    ? `№${baseRcpQ.data.document_number} від ${new Date(baseRcpQ.data.document_date).toLocaleDateString('uk-UA')}`
                    : ''}
                </a>
                {!readOnly && (
                  <button type="button" className="btn-x"
                    onClick={() => setF((s) => ({ ...s, base_receipt_id: '', items: [] }))}
                    title="Зняти звʼязок з приходом і очистити рядки">×</button>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'end', margin: '0.25rem 0 0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <input type="checkbox" checked={f.is_registered_erpn} disabled={readOnly}
            style={{ width: 'auto' }}
            onChange={(e) => setF((s) => ({ ...s, is_registered_erpn: e.target.checked }))} />
          зареєстрована постачальником в ЄРПН
        </label>
        {f.is_registered_erpn && (
          <div className="field" style={{ margin: 0, width: 170 }}>
            <label>Дата реєстрації</label>
            <DatePopover value={f.registration_date} disabled={isPosted}
              onChange={(v) => setF((s) => ({ ...s, registration_date: v }))} />
          </div>
        )}
        {!f.is_registered_erpn && (
          <span style={{ color: 'var(--badge-amber-fg)', fontSize: '0.85rem' }}>
            ⚠ без реєстрації в ЄРПН права на податковий кредит немає
          </span>
        )}
        {!isNew && d?.is_correction && (
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', paddingBottom: 4 }}>
            РК до ПН:{' '}
            {corr?.corrected_number ? (
              correctedDoc ? (
                <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                  title="Відкрити кориговану ПН"
                  onClick={() => navigate(`/tax-invoices-incoming/${correctedDoc.id}`)}>
                  №{corr.corrected_number}
                  {corr.corrected_date
                    ? ` від ${new Date(corr.corrected_date).toLocaleDateString('uk-UA')}`
                    : ''}
                </a>
              ) : (
                <>№{corr.corrected_number}
                  {corr.corrected_date
                    ? ` від ${new Date(corr.corrected_date).toLocaleDateString('uk-UA')}`
                    : ''}</>
              )
            ) : 'зʼявиться після наступної синхронізації з ЄРПН'}
            {' '}(суми — зі знаком коригування)
          </span>
        )}
      </div>

      </DocBlock>

      <DocBlock index={3} title="Рядки"
        actions={!readOnly ? (<>
          <button type="button" className="btn-doc" onClick={() => setBatchNomOpen(true)}
            title="Створити номенклатуру з рядків ПН (предзаповнено з «Опис» + УКТЗЕД)">
            <DocIcon />створити номенклатуру
          </button>
          <button type="button" className="primary" onClick={addRow}>+ рядок</button>
        </>) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th style={{ minWidth: 180 }}>Номенклатура</th>
            <th style={{ minWidth: 160 }}>Опис</th>
            <th style={{ width: 110 }}>УКТЗЕД</th>
            <th style={{ width: 85, textAlign: 'right' }}>К-сть</th>
            <th style={{ width: 100, textAlign: 'right' }}>Ціна без ПДВ</th>
            <th style={{ width: 95 }}>Ставка</th>
            <th style={{ width: 95, textAlign: 'right' }}>База</th>
            <th style={{ width: 90, textAlign: 'right' }}>ПДВ</th>
            {!readOnly && <th style={{ width: 30 }}></th>}
          </tr>
        </thead>
        <tbody>
          {f.items.map((r, i) => {
            const { base, vat } = lineAmounts(r);
            return (
              <tr key={r._key}>
                <td>{i + 1}</td>
                <td>
                  <NomenclatureSelect value={r.nomenclature_id ?? ''} disabled={readOnly}
                    onChange={(nid) => setRow(r._key, {
                      nomenclature_id: nid || null,
                      description: r.description || (nomById.get(nid)?.name ?? ''),
                    })} />
                </td>
                <td>
                  <input value={r.description} disabled={readOnly}
                    onChange={(e) => setRow(r._key, { description: e.target.value })} />
                </td>
                <td>
                  <input value={r.ukt_zed_code ?? ''} disabled={readOnly}
                    placeholder="—"
                    onChange={(e) => setRow(r._key, { ukt_zed_code: e.target.value || null })} />
                </td>
                <td>
                  <input type="number" min="0" step="0.001" value={r.quantity}
                    disabled={readOnly} style={{ textAlign: 'right' }}
                    onChange={(e) => setRow(r._key, { quantity: e.target.value })} />
                </td>
                <td>
                  <input type="number" min="0" step="0.01" value={r.price_without_vat}
                    disabled={readOnly} style={{ textAlign: 'right' }}
                    onChange={(e) => setRow(r._key, { price_without_vat: e.target.value })} />
                </td>
                <td>
                  <select value={r.vat_rate_id} disabled={readOnly}
                    onChange={(e) => setRow(r._key, { vat_rate_id: e.target.value })}>
                    {rates.map((vr) => (
                      <option key={vr.id} value={vr.id}>{vr.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {/* ч.891: підсумковий рядок (кількість 0) — база редагується прямо. */}
                  {!readOnly && (Number(r.quantity) || 0) === 0 ? (
                    <input type="number" min="0" step="0.01" value={r.base_amount ?? ''}
                      style={{ textAlign: 'right' }} placeholder="сума"
                      onChange={(e) => setRow(r._key, { base_amount: e.target.value, vat_amount: null })} />
                  ) : fmt(base)}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(vat)}</td>
                {!readOnly && (
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ cursor: 'pointer', color: '#dc2626' }}
                      title="прибрати рядок"
                      onClick={() => setF((s) => ({
                        ...s, items: s.items.filter((x) => x._key !== r._key),
                      }))}>×</span>
                  </td>
                )}
              </tr>
            );
          })}
          {f.items.length === 0 && (
            <tr><td colSpan={9} style={{ color: 'var(--text-secondary)' }}>
              Немає рядків — додайте вручну або «заповнити з приходу».
            </td></tr>
          )}
        </tbody>
      </TchTable>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <table className="tch" style={{ width: 'auto', minWidth: 300 }}>
          <tbody>
            <tr><td>Разом без ПДВ</td>
              <td style={{ textAlign: 'right' }}>{fmt(totals.base)}</td></tr>
            <tr><td>ПДВ (кредит)</td>
              <td style={{ textAlign: 'right' }}>{fmt(totals.vat)}</td></tr>
            <tr style={{ fontWeight: 600, fontSize: '1rem' }}>
              <td>Усього з ПДВ</td>
              <td style={{ textAlign: 'right' }}>{fmt(totals.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {batchNomOpen && (
        <QuickNomenclatureBatchModal
          initialRows={(() => {
            // Рядки без привʼязаної номенклатури, дедуп однакових
            // (назва+УКТЗЕД) — у ПН бувають повтори одного товару.
            const map = new Map<string, BatchNomInitialRow>();
            for (const r of f.items) {
              if (r.nomenclature_id || !r.description.trim()) continue;
              const k = `${r.description.trim().toLowerCase()}|${r.ukt_zed_code ?? ''}`;
              const ex = map.get(k);
              if (ex) ex.keys.push(r._key);
              else map.set(k, {
                keys: [r._key],
                name: r.description.trim(),
                uktZed: r.ukt_zed_code ?? null,
                vatRateId: r.vat_rate_id || null,
              });
            }
            return [...map.values()];
          })()}
          onClose={() => setBatchNomOpen(false)}
          onCreated={(created) => {
            setF((s) => ({
              ...s,
              items: s.items.map((r) => {
                const hit = created.find((c) => c.keys.includes(r._key));
                return hit && !r.nomenclature_id
                  ? { ...r, nomenclature_id: hit.nom.id }
                  : r;
              }),
            }));
          }}
        />
      )}

      {rcpPopup && (
        <div style={{
          position: 'fixed', inset: 0, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60,
        }} onClick={() => setRcpPopup(false)}>
          <div style={{
            background: 'var(--bg-panel)', borderRadius: 10, padding: '1rem',
            width: 720, maxHeight: '75vh', overflow: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Оберіть проведений прихід</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setRcpPopup(false)}>закрити</button>
            </div>
            {receiptsQ.isLoading && <div>Завантаження…</div>}
            {!receiptsQ.isLoading && !(receiptsQ.data?.items ?? []).length && (
              <div style={{ color: 'var(--text-secondary)' }}>Немає проведених приходів.</div>
            )}
            {(receiptsQ.data?.items ?? []).length > 0 && (
              <TchTable>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Дата</th>
                    <th style={{ textAlign: 'right' }}>Сума</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(receiptsQ.data?.items ?? []).map((rc: any) => (
                    <tr key={rc.id} style={{ cursor: 'pointer' }}
                      onDoubleClick={() => fillFromReceipt(rc.id)}>
                      <td>{rc.document_number}</td>
                      <td>
                        {rc.document_date ? new Date(rc.document_date).toLocaleDateString('uk-UA') : ''}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmt(rc.total_amount)}</td>
                      <td>
                        <button onClick={() => fillFromReceipt(rc.id)}>обрати</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            )}
          </div>
        </div>
      )}

      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={f.comment} disabled={readOnly}
            onChange={(e) => setF((s) => ({ ...s, comment: e.target.value }))} />
        </div>
        <div>
          {/* Вкладення (рішення власника 2026-08-26). */}
          <AttachmentsPanel organizationId={f.organization_id}
            documentType={'tax.tax_invoices_incoming'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}
